/**
 * The country page's header: back link; one title row — flag, country,
 * plan state — with the plan actions aligned to its right (Notifications
 * bell, plan on/off, Kill, Generate); one row of fact tiles underneath
 * (window and layout time — a click edits them —, Auto, Auto-send, the
 * route tests); the campaign groups' auto-send table when opened (Phase
 * 20); the country's technical halt banner and one banner per group halted
 * today; a notice with "Set the timezone" while the country has none.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { DayPlanPreview } from '@trafficpulse/shared';

import { AUTOMATION_PATH } from '@/features/drop-generator/utils/automation-paths';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import type { DropGenPlanDetail } from '../../drop-generator.types';
import { useSchedulerStatus } from '../../useDropGenerator';
import { derivePlanStatus } from '../../utils/plan-status';
import { PlanStatusBadge } from '../shared/StateBadges';
import { HeaderActions } from './HeaderActions';
import { PlanFactsBar } from './schedule/PlanFactsBar';
import { AutoSendHaltBanner } from './autosend/AutoSendHaltBanner';
import { AutoSendGroupsPanel } from './autosend/groups/AutoSendGroupsPanel';
import { GroupHaltBanner } from './autosend/groups/GroupHaltBanner';
import { groupRows } from './autosend/groups/group-rows';
import type { AutoSendLayer } from './autosend/settings/autosend-draft';
import { NotificationsDialog } from './notifications/NotificationsDialog';
import { PlanWindowDialog } from './times/PlanWindowDialog';
import { TimezoneNotice } from './times/TimezoneNotice';

interface Props {
  countryName: string;
  plan: DropGenPlanDetail;
  preview: DayPlanPreview | undefined;
  /** A campaign group's gear: its master config, on the Auto-send tab. */
  onOpenGroupAutoSend: (masterId: string) => void;
  /** 19/10/2026 — a container's editor (the route-test tile opens its test). */
  onOpenContainer?: (id: string) => void;
  /** 19/10/2026 — the Errors tab (the Errors tile opens it). */
  onOpenErrors?: () => void;
  /** Inside another page (the SMSWiser Automation tab): no back link, the
   *  country is already the page's title — the plan's state stays. */
  embedded?: boolean;
}

export const CountryHeader: FC<Props> = ({ countryName, plan, preview, onOpenGroupAutoSend, onOpenContainer, onOpenErrors, embedded = false }) => {
  const [editingTimes, setEditingTimes] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const scheduler = useSchedulerStatus().data;
  const mine = scheduler?.plans.find((p) => p.countryKey === plan.countryKey);
  const server = scheduler?.sendModes;
  const groups = groupRows(plan, mine);
  const onSettings = (layer: AutoSendLayer) => onOpenGroupAutoSend(layer.id);
  return (
    <section className="flex flex-col gap-3">
      {embedded ? null : (
        <Link to={AUTOMATION_PATH} className="inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Automation
        </Link>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        {embedded ? (
          <PlanStatusBadge status={derivePlanStatus(plan)} recipientTimezone={plan.recipientTimezone} short />
        ) : (
          <h1 className="flex min-w-0 items-center gap-3 text-2xl font-bold">
            <span className="truncate">
              {countryToFlagEmoji(countryName)} {countryName}
            </span>
            <PlanStatusBadge status={derivePlanStatus(plan)} recipientTimezone={plan.recipientTimezone} short />
          </h1>
        )}
        <HeaderActions plan={plan} preview={preview} onNotifications={() => setNotifying(true)} />
      </div>
      <PlanFactsBar
        plan={plan}
        preview={preview}
        onEditTimes={() => setEditingTimes(true)}
        groupsOpen={groupsOpen}
        onToggleGroups={() => setGroupsOpen((o) => !o)}
        onOpenContainer={onOpenContainer}
        onOpenErrors={onOpenErrors}
      />
      {groupsOpen ? (
        <div className="duration-200 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none">
          <AutoSendGroupsPanel plan={plan} status={mine} server={server} onSettings={onSettings} />
        </div>
      ) : null}
      <AutoSendHaltBanner plan={plan} stuckAtUtc={mine?.stuckAtUtc ?? []} />
      {groups.filter((g) => g.haltedToday).map((g) => (
        <GroupHaltBanner key={g.masterId} countryKey={plan.countryKey} group={g} onSettings={onSettings} />
      ))}
      {plan.planIssues.includes('tz_missing') ? <TimezoneNotice onSet={() => setEditingTimes(true)} /> : null}
      <PlanWindowDialog
        open={editingTimes}
        countryKey={plan.countryKey}
        recipientTimezone={plan.recipientTimezone}
        windowStartMin={plan.windowStartMin}
        windowEndMin={plan.windowEndMin}
        generateAtMin={plan.generateAtMin}
        containers={plan.containers}
        onClose={() => setEditingTimes(false)}
      />
      <NotificationsDialog
        open={notifying}
        countryKey={plan.countryKey}
        countryName={countryName}
        recipientTimezone={plan.recipientTimezone}
        generateAtMin={plan.generateAtMin}
        windowEndMin={plan.windowEndMin}
        onClose={() => setNotifying(false)}
      />
    </section>
  );
};
